import { alertErrorHandler, NotificationsAction, showSuccess } from '@spike/notifications-action';
import ApiClient, { createTokenConfig, isCancelled } from 'api/ApiClient';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import store from 'store';
import {
    DataExportAction,
    DATA_EXPORT_START,
    DATA_EXPORT_SUCCESS,
    DATA_EXPORT_ERROR,
    DATA_EXPORT_RESET
} from './DataExportActionsTypes';

/**
 * Export type options
 */
export type ExportType = 'customers' | 'invoices' | 'payments' | 'bundle' | 'products_services';

/**
 * Export parameters interface
 */
export interface ExportParams {
    type: ExportType;
    startDate: string; // ISO date string (YYYY-MM-DD)
    endDate: string; // ISO date string (YYYY-MM-DD)
}

/**
 * Thunk action to export data to  ZIP
 * Downloads the file directly from the API
 * - For 'bundle' type: downloads a ZIP file containing multiple CSVs
 * - For other types: downloads a single ZIP file
 */
export const exportDataThunk = (
    params: ExportParams
): ThunkAction<void, RootState, null, DataExportAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(exportStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const token = store.getState().login.auth.token;

        // Build URL with query parameters
        const url = `/exports/quickbooks/${params.type}?marketplace_id=${marketplaceId}&start_at=${params.startDate}&end_at=${params.endDate}`;

        try {
            const response = await ApiClient.get(url, {
                ...createTokenConfig(token!),
                responseType: 'blob'
            });

            const mimeType = 'application/zip';
            const fileExtension = 'zip';

            // Create blob and check zip content for data
            const blob = new Blob([response.data], { type: mimeType });

            // Trigger file download
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute(
                'download',
                `${params.type}_export_${params.startDate}_to_${params.endDate}.${fileExtension}`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            dispatch(exportSuccess());
            dispatch(showSuccess('Export downloaded successfully'));
        } catch (error) {
            if (!isCancelled(error)) {
                dispatch(exportError('Failed to export data'));
                alertErrorHandler(error, dispatch, 'Error exporting data');
            }
        }
    };
};

/**
 * Action creators
 */
const exportStart = (): DataExportAction => {
    return {
        type: DATA_EXPORT_START
    };
};

const exportSuccess = (): DataExportAction => {
    return {
        type: DATA_EXPORT_SUCCESS
    };
};

const exportError = (error: string): DataExportAction => {
    return {
        type: DATA_EXPORT_ERROR,
        payload: { error }
    };
};

/**
 * Reset export state
 */
export const resetExport = (): DataExportAction => {
    return {
        type: DATA_EXPORT_RESET
    };
};
